import { useMemo, useState, useEffect, useRef } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { findIngredient, fuzzyMatchIngredient } from "../data/ingredients";
import { recipeNutrition, recipeNutritionBreakdown } from "../lib/nutrition";
import { applyCookSessionToRecipe, canonicalizeEffectiveRecipe, countActiveSwaps } from "../lib/effectiveRecipe";
import { reheatToCookInstructions } from "../lib/reheatToCookInstructions";
import CookCompleteSummary from "./CookCompleteSummary";
import CookKitchenUpdate from "./CookKitchenUpdate";
import CookRateIt from "./CookRateIt";
import CookReviseOffer from "./CookReviseOffer";
import { useToast } from "../lib/toast";
import { autoMigratePatchOnOpen, openedNounForState } from "../lib/pantryHelpers";
import { buildInitialUsedItems, buildRemovalPlan } from "../lib/cookSessionPlan";

// Re-export the planners for any callers that still pull them from
// CookComplete (tests, adjacent screens). Pure logic now lives in
// src/lib/cookSessionPlan.js — extracted 2026-05-03 to keep this
// component below the file-size tripwire.
export { buildInitialUsedItems, buildRemovalPlan };

// Visibility gate for the per-ingredient calorie breakdown phase. The
// `nutritionDebug` phase is a diagnostic screen that exposes the
// macro-resolver trace (canonical match, factor, why a row was
// skipped) and reads as engineering output to end users. Gated behind
// a localStorage flag so day-to-day cooks go straight from rating to
// save without seeing it. Flip on when debugging a dashboard
// regression:
//     localStorage.setItem("mise_debug", "1")
const SHOW_NUTRITION_DEBUG = typeof localStorage !== "undefined"
  && localStorage.getItem("mise_debug") === "1";

// Completion flow shown when the user taps the final "DONE! LOG IT"
// button in CookMode. The 2026-06 collapse cut the old 7-phase quiz
// (ingredientsUsed → confirmRemoval → leftovers → leftoverLocation →
// diners → rating → notes) down to three:
//
//   1. celebrate     — confetti + "+XP" pulse, "Continue →"
//   2. kitchenUpdate — ONE reconcile screen (CookKitchenUpdate):
//                      per-ingredient deductions seeded from mid-cook
//                      counter-list check-offs, live "leaves N" math,
//                      inline unit-mismatch fixing, OUT → ADD TO LIST
//                      chips, leftovers folded in.
//   3. rateIt        — ONE social screen (CookRateIt): diners +
//                      rating + notes + servings-per-eater.
//
// On save, we insert one row into `cook_logs`, apply the kitchen
// mutations in a single setPantry, and surface an UNDO toast that can
// restore every touched row. The DB's INSERT trigger fans out a
// rating-aware notification to every diner. Positive ratings mark the
// row as a favorite so the Cookbook can surface it immediately.
//
// Props:
//   recipe          — the recipe being logged (needs slug, title, etc.)
//   userId          — chef's user id (for the row's user_id)
//   family/friends  — [{ otherId, other: { name, ... } }] connections
//   setShoppingList — shopping-list setter for the OUT chips (optional)
//   onFinish()      — called after the row is saved (or the user bailed).
//
// Copy is deliberately warm — a finished meal is a celebration, not a form.

const RATINGS = [
  { id: "rough",  emoji: "😬", label: "Rough one",   color: COLOR.rose, bg: COLOR.roseDeep, border: COLOR.roseDim },
  { id: "meh",    emoji: "😐", label: "Meh",         color: COLOR.dim,    bg: COLOR.soil, border: COLOR.border },
  { id: "good",   emoji: "😊", label: "Pretty good", color: COLOR.leaf, bg: COLOR.leafDeep, border: COLOR.leafDim },
  { id: "nailed", emoji: "🤩", label: "Nailed it",   color: COLOR.gold, bg: COLOR.goldDeep, border: COLOR.goldDim },
];

// Sum up the "xp per cook" weights on the recipe's skills so the celebration
// screen can show a pulsing "+N XP" number. If the recipe has no skill block,
// fall back to a token 10 XP so there's something to land on.
function totalXpForRecipe(recipe) {
  const skills = recipe?.skills;
  if (!Array.isArray(skills) || skills.length === 0) return 10;
  return skills.reduce((sum, s) => sum + (Number(s.xp) || 0), 0);
}

export default function CookComplete({ recipe, userId, family = [], friends = [], pantry = [], setPantry, setShoppingList = null, ingredientInfo, brandNutrition, onFinish, cookSession = null, telemetrySessionId = null, onForkRecipe = null, onReviseRecipe = null }) {
  const { push: pushToast } = useToast();
  const [phase, setPhase] = useState("celebrate");
  const [selectedDiners, setSelectedDiners] = useState(() => new Set());
  const [rating, setRating] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Log-on-finish: the cook_log id created the instant this screen
  // mounts (see effect below). save() ENRICHES this row instead of
  // inserting a fresh one, so a cook is logged the moment it's finished
  // — even if the user never taps through to the final SAVE.
  const [ensuredLogId, setEnsuredLogId] = useState(null);
  const ensureRanRef = useRef(false);

  // Create (or adopt) the cook_log the moment this screen opens — i.e.
  // the instant the cook is finished. This is the fix for the #1 "my
  // cook is nowhere" bug: previously the log was inserted only at the
  // very end, behind the final SAVE tap, so anyone who didn't complete
  // the multi-screen flow ended up with a "finished" session and NO
  // log. Now the row exists immediately; save() enriches it.
  //
  // Idempotent: adopts an already-linked log if the session has one
  // (resume / StrictMode double-mount), and the ref guards a single run.
  // Rating starts null ("logged, not yet rated") until the rate-it
  // screen fills it in — display falls back gracefully and is_favorite
  // stays false for null (migration 0274 made rating nullable).
  useEffect(() => {
    if (ensureRanRef.current) return;
    if (!userId || !recipe?.slug) return;
    ensureRanRef.current = true;
    (async () => {
      try {
        if (telemetrySessionId) {
          const { data: sess } = await supabase
            .from("cook_sessions")
            .select("cook_log_id")
            .eq("id", telemetrySessionId)
            .maybeSingle();
          if (sess?.cook_log_id) { setEnsuredLogId(sess.cook_log_id); return; }
        }
        const communityRecipeId = recipe._communityRecipeId || recipe.communityRecipeId || null;
        // Enrich the at-finish stub with XP + nutrition NOW, not only
        // inside save(). The rate-it flow gets skipped or auto-dismissed
        // often enough that ~half of cooks were landing as BLANK logs
        // (xp 0, no macros) — a finished cook that reads as "failed."
        // Stamping them here means the cooking log + nutrition dashboard
        // are complete even when the cook never taps through to rate;
        // save() later only ADDS the optional star rating + diners on top
        // (it re-writes these same values idempotently).
        const stubXp = (() => {
          try { return Number(totalXpForRecipe(recipe)) || 0; } catch { return 0; }
        })();
        const stubNutrition = (() => {
          try {
            const nn = recipeNutrition
              ? recipeNutrition(recipe, { pantry, brandNutrition, getInfo: ingredientInfo?.getInfo })
              : null;
            return (nn && nn.coverage?.resolved > 0)
              ? { ...nn.perServing, coverage: nn.coverage }
              : null;
          } catch { return null; }
        })();
        const { data: row, error: insErr } = await supabase
          .from("cook_logs")
          .insert({
            user_id:        userId,
            recipe_slug:    recipe.slug,
            recipe_title:   recipe.title,
            recipe_emoji:   recipe.emoji || "🍽️",
            recipe_cuisine: recipe.cuisine || null,
            recipe_category: recipe.category || null,
            rating:         null,        // unrated until the rate-it screen fills it in
            xp_earned:      stubXp,
            nutrition:      stubNutrition,
            diners:         [],
            is_favorite:    false,
            servings_per_eater: 1,
            ...(communityRecipeId ? { community_recipe_id: communityRecipeId } : {}),
          })
          .select("id")
          .single();
        if (insErr) {
          // Leave ensuredLogId null → save() falls back to a fresh insert.
          console.warn("[cook_logs] ensure-on-finish insert failed:", insErr);
          return;
        }
        if (row?.id) {
          setEnsuredLogId(row.id);
          if (telemetrySessionId) {
            supabase.from("cook_sessions")
              .update({ cook_log_id: row.id })
              .eq("id", telemetrySessionId)
              .then(({ error: linkErr }) => {
                if (linkErr) console.warn("[cook_logs] ensure session-link failed:", linkErr);
              });
          }
        }
      } catch (e) {
        console.warn("[cook_logs] ensure-on-finish threw:", e?.message || e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Award_xp breakdown captured during save() — drives the
  // beat-sequenced summary in CookCompleteSummary (P5-5).
  const [xpBreakdown, setXpBreakdown] = useState(null);
  // The cook_log id we just inserted — handed to the summary
  // overlay so it can fetch every related xp_events row.
  const [summaryCookLogId, setSummaryCookLogId] = useState(null);
  // How many recipe-servings each eater consumed. Default 1 covers the
  // common case; the stepper lives on the rate-it screen.
  const [servingsPerEater, setServingsPerEater] = useState(1);
  // usedItems captures the user's "what did I actually use" decisions on
  // the kitchen-update screen. Seeded once from the initial pantry
  // snapshot; realtime pantry changes during the flow are intentionally
  // ignored so the user's edits don't flip out from under them.
  //
  // The recipe handed to buildInitialUsedItems goes through
  // applyCookSessionToRecipe FIRST so swap overrides from CookMode
  // carry into the deduction screen, and the session itself seeds
  // checkoffState from the mid-cook counter-list ticks — what the
  // cook already confirmed in the kitchen never gets asked twice.
  const [usedItems, setUsedItems] = useState(() => {
    const effective = applyCookSessionToRecipe(recipe, cookSession?.session, pantry) || recipe;
    return buildInitialUsedItems(effective, pantry, cookSession?.session, { ingredientInfo });
  });
  // pickerForIdx is the row whose multi-match picker is currently open (null
  // for closed). Sheet overlays the phase and lets the user choose which
  // kitchen row to draw from when more than one matches the ingredient id.
  const [pickerForIdx, setPickerForIdx] = useState(null);
  // extraRemovals captures "I subbed X for Y" cases: rows the user wants
  // decremented beyond the recipe's own list (e.g. yesterday's leftover
  // chicken standing in for raw). Lives alongside usedItems in the final
  // removal plan.
  const [extraRemovals, setExtraRemovals] = useState([]);
  // Skip-kitchen gate. When the cook chooses to skip the inventory
  // reconciliation (either from the celebrate gate or the in-screen
  // SKIP button), this rides through to the rating screen so the final
  // save() leaves the kitchen untouched — but the user STILL gets to
  // rate the meal. Skipping inventory must never skip rating.
  const [skipKitchen, setSkipKitchen] = useState(false);
  // Leftovers state. Inline on the kitchen-update screen now. Default
  // answers the common case (nothing saved) so the happy path needs no
  // extra tap; compound-produce recipes (sriracha, stock) default to
  // SAVING since the user made the batch specifically to keep it.
  const [leftoverChoice,   setLeftoverChoice]   = useState(() =>
    (recipe?.produces?.kind === "ingredient" && recipe.produces.ingredientId) ? "yes" : "no");
  const [leftoverLocation, setLeftoverLocation] = useState(null);
  const [leftoverMode,     setLeftoverMode]     = useState("fraction");
  const [leftoverFraction, setLeftoverFraction] = useState(0.5);
  const [leftoverServings, setLeftoverServings] = useState(() => {
    const diners = 1;
    return Math.max(1, Number(recipe?.serves || 2) - diners);
  });
  // Optional user-override for the leftover Meal's name. Empty string =
  // auto "Leftover ${recipe.title}".
  const [leftoverCustomName, setLeftoverCustomName] = useState("");

  // Manual canonical-bind for leftovers — when an AI recipe didn't emit
  // a `produces` field but the user knows the output IS a reusable
  // kitchen ingredient (dashi, stock, sauce). { id, name, defaultUnit }.
  const [manualProduces, setManualProduces] = useState(null);
  const [producesPickerOpen, setProducesPickerOpen] = useState(false);
  const [producesSearch, setProducesSearch] = useState("");

  // OUT → ADD TO LIST chips already tapped this session (key =
  // canonicalId or lowercased name). Keeps the chip flipped to ✓ and
  // dedupes repeat taps.
  const [shoppingAddedKeys, setShoppingAddedKeys] = useState(() => new Set());

  // `effectiveProduces` blends the recipe's declared `produces` with the
  // user's manual bind. Manual bind wins when the recipe didn't declare
  // anything.
  const effectiveProduces = useMemo(() => {
    if (recipe?.produces?.kind === "ingredient" && recipe.produces.ingredientId) {
      return recipe.produces;
    }
    if (manualProduces) {
      return {
        kind: "ingredient",
        ingredientId: manualProduces.id,
        yield: {
          amount: Math.max(1, Number(recipe?.serves) || 1),
          unit: manualProduces.defaultUnit || "cup",
        },
        shelfLifeDays: 5,
        freezerShelfLifeDays: 60,
      };
    }
    return null;
  }, [recipe?.produces, manualProduces, recipe?.serves]);
  const isCompoundProduce = effectiveProduces?.kind === "ingredient";

  // Top fuzzy matches for the produces picker.
  const producesMatches = useMemo(() => {
    const needle = producesSearch.trim();
    if (!needle || needle.length < 2) return [];
    return fuzzyMatchIngredient(needle, 8)
      .map((m) => m.ingredient)
      .slice(0, 8);
  }, [producesSearch]);

  // Fork-to-new-recipe state. The user made one or more cook-time swaps
  // and can promote them into a new user_recipes row. Original recipe is
  // left untouched.
  const swapCount = countActiveSwaps(cookSession?.session);
  const canOfferFork = !!onForkRecipe && swapCount > 0;
  const [forkState, setForkState] = useState("idle");
  const [forkName,  setForkName]  = useState(() => {
    const base = String(recipe?.title || "Recipe").trim();
    return `${base} v2`;
  });
  const [forkOpen,  setForkOpen]  = useState(false);
  const [forkError, setForkError] = useState(null);
  const handleForkSave = async () => {
    if (!onForkRecipe) return;
    setForkState("pending");
    setForkError(null);
    try {
      // Materialize the ephemeral session into a concrete recipe
      // object. canonicalizeEffectiveRecipe strips the UI-only markers
      // (_swappedFrom, _skipped, _extra) so the fork reads as clean
      // canon on its next cook.
      const promoted = applyCookSessionToRecipe(recipe, cookSession?.session, pantry);
      const canonical = canonicalizeEffectiveRecipe(promoted);
      const forked = {
        ...canonical,
        title: forkName.trim() || `${recipe.title || "Recipe"} v2`,
        // Mint a fresh slug base from the new title; saveRecipe
        // will uniqueify it per-user.
        slug:  undefined,
        forkedFrom: {
          slug:   recipe?.slug || null,
          title:  recipe?.title || null,
          source: recipe?.source || null,
        },
      };
      // source='custom' — the user authored this iteration, even if
      // the parent was an AI draft.
      await onForkRecipe(forked, "custom");
      setForkState("saved");
      setTimeout(() => setForkOpen(false), 1200);
    } catch (e) {
      console.error("[CookComplete] fork save failed:", e);
      setForkError(e?.message || "Couldn't save");
      setForkState("idle");
    }
  };

  const xp = useMemo(() => totalXpForRecipe(recipe), [recipe]);
  // Merge family + friends, dedupe by otherId, preserve family-first order.
  const connections = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const row of [...family, ...friends]) {
      if (!row?.otherId || seen.has(row.otherId)) continue;
      seen.add(row.otherId);
      const name = row.other?.name || "Friend";
      out.push({
        id: row.otherId,
        name,
        first: name.split(/\s+/)[0],
        kind: row.kind,                        // "family" | "friend"
        initial: (name[0] || "?").toUpperCase(),
        avatarUrl: row.other?.avatar_url || null,
      });
    }
    return out;
  }, [family, friends]);

  const toggleDiner = (id) => setSelectedDiners(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // OUT chip handler — one tap re-adds an emptied canonical to the
  // shopping list. Same row shape + dedupe key as CookMode's
  // addOneToShoppingList so receipt-scan bias and list merging behave
  // identically regardless of which surface added the row.
  const addOutToShopping = (item) => {
    if (!setShoppingList) return;
    setShoppingList(prev => {
      const existing = new Set(prev.map(i => i.ingredientId || i.name.toLowerCase()));
      const canonical = item.ingredientId ? findIngredient(item.ingredientId) : null;
      const key = item.ingredientId || item.name.toLowerCase();
      if (existing.has(key)) return prev;
      return [...prev, {
        id: crypto.randomUUID(),
        ingredientId: item.ingredientId || null,
        name: canonical?.name || item.name,
        emoji: canonical?.emoji || item.emoji || "🥫",
        amount: 1,
        unit: item.unit || "unit",
        category: canonical?.category || "pantry",
        source: "recipe",
      }];
    });
    setShoppingAddedKeys(prev => new Set(prev).add(item.key));
  };

  // skipKitchen=true logs the meal (cook_log + XP + rating/notes) but
  // leaves the kitchen untouched — no deduction, no leftover row.
  // Drives the "Skip — keep my kitchen" bypass available on every
  // phase, for cooks who don't want amounts auto-reduced.
  const save = async ({ skipKitchen = false } = {}) => {
    setSaving(true);
    setError(null);

    // 1) Insert the cook_log first so we have its id for source_cook_log_id
    //    back-references on any kitchen rows we're about to create. Errors
    //    here abort everything — we never want to mutate the kitchen against
    //    a cook that didn't log.
    const n = (recipe && recipeNutrition)
      ? recipeNutrition(recipe, { pantry, brandNutrition, getInfo: ingredientInfo?.getInfo })
      : null;
    const nutritionBlob = (n && n.coverage.resolved > 0)
      ? { ...n.perServing, coverage: n.coverage }
      : null;
    // Community attribution — threads cook_logs.community_recipe_id
    // (migration 0236) so this cook counts as a leaderboard vote.
    const communityRecipeId = recipe._communityRecipeId
      || recipe.communityRecipeId
      || null;
    const payload = {
      user_id: userId,
      recipe_slug:    recipe.slug,
      recipe_title:   recipe.title,
      recipe_emoji:   recipe.emoji || "🍽️",
      recipe_cuisine: recipe.cuisine || null,
      recipe_category: recipe.category || null,
      rating,
      notes: notes.trim() || null,
      xp_earned: xp,
      diners: [...selectedDiners],
      is_favorite: rating === "good" || rating === "nailed",
      nutrition:           nutritionBlob,
      ...(communityRecipeId ? { community_recipe_id: communityRecipeId } : {}),
      // Zero is a valid value ("saved it all"), so we guard with
      // Number.isFinite rather than the || 1 fallback which would
      // collapse a deliberate 0 into 1.
      servings_per_eater:  Number.isFinite(Number(servingsPerEater)) ? Number(servingsPerEater) : 1,
    };
    // The row almost always already exists — it was created when this
    // screen mounted (ensuredLogId). save() ENRICHES it with the rating/
    // notes/diners/nutrition the user just chose. Only insert fresh when
    // the at-finish create didn't land (telemetry never started, or that
    // insert errored).
    let cookLogId = ensuredLogId;
    let logErr = null;
    if (cookLogId) {
      const { error } = await supabase
        .from("cook_logs")
        .update(payload)
        .eq("id", cookLogId);
      logErr = error;
    } else {
      // Belt-and-braces: if the at-finish insert landed but its state
      // update hadn't flushed yet (fast tap-through), the session is
      // already linked — adopt that row and UPDATE rather than insert a
      // duplicate. Only costs a read on this rare fallback path.
      if (telemetrySessionId) {
        const { data: sess } = await supabase
          .from("cook_sessions")
          .select("cook_log_id")
          .eq("id", telemetrySessionId)
          .maybeSingle();
        if (sess?.cook_log_id) {
          const { error } = await supabase
            .from("cook_logs")
            .update(payload)
            .eq("id", sess.cook_log_id);
          if (error) {
            console.error("[cook_logs] save failed:", error);
            setError(error.message || "Couldn't save. Try again?");
            setSaving(false);
            return;
          }
          cookLogId = sess.cook_log_id;
        }
      }
    }
    if (!cookLogId) {
      const { data: logRow, error } = await supabase
        .from("cook_logs")
        .insert(payload)
        .select("id")
        .single();
      logErr = error;
      cookLogId = logRow?.id || null;
      // Link the cook_sessions row (migration 0136) to this cook_log so
      // cook_duration_stats can JOIN cleanly. The ensured-row path
      // already linked at mount, so only the insert-fallback needs this.
      if (!logErr && cookLogId && telemetrySessionId) {
        supabase.from("cook_sessions")
          .update({ cook_log_id: cookLogId })
          .eq("id", telemetrySessionId)
          .then(({ error: linkErr }) => {
            if (linkErr) console.warn("[cook_logs] session link failed:", linkErr);
          });
      }
    }
    if (logErr) {
      console.error("[cook_logs] save failed:", logErr);
      setError(logErr.message || "Couldn't save. Try again?");
      setSaving(false);
      return;
    }

    // 1b) Fire the XP ledger via award_xp() and AWAIT the breakdown —
    //     it drives the beat-sequenced reveal in CookCompleteSummary.
    //     Failures are soft.
    if (cookLogId) {
      const { data: breakdown, error: xpErr } = await supabase.rpc("award_xp", {
        p_user_id:   userId,
        p_source:    "cook_complete",
        p_ref_table: "cook_logs",
        p_ref_id:    cookLogId,
      });
      if (xpErr) {
        console.error("[award_xp] cook_complete failed:", xpErr);
      } else {
        setXpBreakdown(breakdown || null);
      }
    }

    // 2) Apply kitchen mutations in a single setPantry call. useSyncedList
    //    diffs prev vs next and fires INSERT / UPDATE / DELETE behind the
    //    scenes, so we just return the post-cook array.
    //
    // Everything the mutation touches is recorded for the batch UNDO
    // toast: prevRows holds the pre-cook row object for every modified
    // row (first write wins, so cascades + leftover merges restore to
    // the true original), inserts holds rows the cook deleted, removes
    // holds leftover rows the cook created. Undo replays the inverse.
    const cookMigrations = [];
    const undo = { prevRows: new Map(), inserts: [], removes: new Set() };
    let leftoverSaved = false;
    if (setPantry && !skipKitchen) {
      const plan = buildRemovalPlan(usedItems, extraRemovals, pantry);
      setPantry(prev => {
        // Updater can re-run (StrictMode); rebuild the undo ledger from
        // scratch each pass so ops aren't double-counted.
        undo.prevRows.clear(); undo.inserts.length = 0; undo.removes.clear();
        const recordPrev = (row) => {
          if (!undo.prevRows.has(row.id)) undo.prevRows.set(row.id, row);
        };
        const byId = new Map(prev.map(r => [r.id, r]));

        // 2a) Decrements from the removal plan. Non-convertible entries
        //     can't reach here anymore — the kitchen-update screen
        //     blocks its CTA until every mismatch is fixed or the row
        //     is explicitly skipped — but the guard stays as a backstop
        //     so a stale plan can never produce a garbage write.
        for (const entry of plan) {
          if (!entry.convertible) continue;
          const row = byId.get(entry.pantryRowId);
          if (!row) continue;
          // Protected keepsake rows (migration 0044) never get deleted
          // or decremented by a cook.
          if (row.protected) continue;
          if (entry.newAmount <= 0) {
            // Package-mode rows (migration 0054): pop the next sealed
            // unit into the open slot instead of deleting. Only delete
            // when we're truly out of everything.
            if (row.packageAmount != null && row.reserveCount > 0) {
              recordPrev(row);
              byId.set(row.id, {
                ...row,
                reserveCount: row.reserveCount - 1,
                amount: Number(row.packageAmount),
              });
            } else {
              undo.inserts.push(row);
              byId.delete(row.id);
            }
          } else {
            // Seal-break migration — if the cook decrement crosses
            // the seal on a fridge-after-open state, also patch
            // location + tile so the row physically migrates.
            const migrate = autoMigratePatchOnOpen({
              prevAmount: row.amount,
              newAmount:  entry.newAmount,
              max:        row.max,
              state:      row.state,
              location:   row.location,
            });
            recordPrev(row);
            if (migrate) {
              byId.set(row.id, {
                ...row,
                amount:   entry.newAmount,
                location: migrate.location,
                tileId:   migrate.tileId,
              });
              cookMigrations.push({
                pantryRowId:      row.id,
                state:            row.state,
                previousLocation: row.location,
                previousTileId:   row.tileId || null,
              });
            } else {
              byId.set(row.id, { ...row, amount: entry.newAmount });
            }
          }
        }

        // 2b) Leftover row creation. Compound-produce recipes create or
        //     merge a kind='ingredient' row; regular meals create a
        //     fresh kind='meal' row keyed by source_cook_log_id.
        const wantsLeftover = leftoverChoice === "yes"
          && leftoverLocation
          && leftoverLocation !== "garbage";
        leftoverSaved = wantsLeftover;
        if (wantsLeftover) {
          const now = new Date();
          const produces = effectiveProduces;
          const fraction = leftoverMode === "fraction"
            ? leftoverFraction
            : Math.min(1, Math.max(0, leftoverServings / (Number(recipe?.serves) || 1)));

          if (produces?.kind === "ingredient" && produces.ingredientId) {
            const canonical = findIngredient(produces.ingredientId);
            const yieldAmount = Number(produces.yield?.amount) || 1;
            const yieldUnit   = produces.yield?.unit || canonical?.defaultUnit || "g";
            const savedAmount = yieldAmount * fraction;
            const shelfDays = leftoverLocation === "freezer"
              ? (produces.freezerShelfLifeDays ?? produces.shelfLifeDays ?? 90)
              : (produces.shelfLifeDays ?? 90);
            const expiresAt = new Date(now.getTime() + shelfDays * 86400000);

            // Merge with an existing same-id same-location row so
            // bought bottle + homemade batch land as one row,
            // earliest-expiry wins per the 0025 merge rule.
            const existing = [...byId.values()].find(r =>
              r.ingredientId === produces.ingredientId &&
              (r.location || "pantry") === leftoverLocation &&
              (r.kind || "ingredient") === "ingredient"
            );
            if (existing) {
              recordPrev(existing);
              byId.set(existing.id, {
                ...existing,
                amount: Number(existing.amount) + savedAmount,
                expiresAt: existing.expiresAt && existing.expiresAt < expiresAt
                  ? existing.expiresAt
                  : expiresAt,
                purchasedAt: now,
                sourceRecipeSlug: recipe.slug,
                sourceCookLogId: cookLogId,
              });
            } else {
              const newId = typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : `pantry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              undo.removes.add(newId);
              // No `category` write — the column is retired (hub chain
              // derives grouping from ingredientId); the DB default
              // satisfies NOT NULL.
              byId.set(newId, {
                id: newId,
                ingredientId: produces.ingredientId,
                name: canonical?.name || produces.ingredientId,
                emoji: canonical?.emoji || recipe.emoji || "🥣",
                amount: savedAmount,
                unit: yieldUnit,
                max: Math.max(yieldAmount, savedAmount),
                lowThreshold: 0.25,
                priceCents: null,
                location: leftoverLocation,
                expiresAt,
                purchasedAt: now,
                kind: "ingredient",
                servingsRemaining: null,
                sourceRecipeSlug: recipe.slug,
                sourceCookLogId: cookLogId,
                ownerId: userId,
              });
            }
          } else {
            // Regular meal: kind='meal' row keyed by cook_log_id so the
            // same cook can't merge into a prior night's carbonara.
            // Shelf-life defaults: fridge 3d, freezer 60d, pantry none.
            const servesCount = Number(recipe?.serves) || 1;
            const savedServings = leftoverMode === "fraction"
              ? servesCount * fraction
              : Math.max(0, leftoverServings);
            const shelfDays = leftoverLocation === "freezer" ? 60
              : leftoverLocation === "fridge" ? 3
              : 0;
            const expiresAt = shelfDays > 0
              ? new Date(now.getTime() + shelfDays * 86400000)
              : null;
            const newId = typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `meal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            undo.removes.add(newId);

            // Compose the leftover meal's flat ingredient list from
            // the removal plan — a leftover lasagna made from leftover
            // marinara inherits tomato / onion / garlic / basil from
            // the marinara row's `ingredients` array.
            const flatSet = new Set();
            for (const entry of plan || []) {
              const row = entry?.pantryRow;
              if (!row) continue;
              const rowIngredientIds = Array.isArray(row.ingredients) && row.ingredients.length
                ? row.ingredients
                : (row.ingredientId ? [row.ingredientId] : []);
              for (const id of rowIngredientIds) if (id) flatSet.add(id);
            }
            const flatIngredientIds = [...flatSet];

            byId.set(newId, {
              id: newId,
              ingredientId: null,
              // Flattened tag set for fast recipe matching.
              ingredients: flatIngredientIds,
              // User-override wins; empty string falls back to the
              // auto-generated "Leftover X".
              name: leftoverCustomName.trim() || `Leftover ${recipe.title}`,
              emoji: recipe.emoji || "🍽️",
              amount: savedServings,
              unit: "serving",
              max: servesCount,
              lowThreshold: 0.25,
              priceCents: null,
              location: leftoverLocation,
              expiresAt,
              purchasedAt: now,
              kind: "meal",
              servingsRemaining: savedServings,
              sourceRecipeSlug: recipe.slug,
              sourceCookLogId: cookLogId,
              // Auto-populate the reheat walkthrough at cook-complete
              // time — a concrete stored block, self-contained even if
              // the source recipe is deleted later.
              cookInstructions: reheatToCookInstructions(recipe),
              ownerId: userId,
            });
          }
        }

        return [...byId.values()];
      });

      // Batch UNDO toast — fires AFTER setPantry returns so the writes
      // are already on their way to Supabase. One tap restores every
      // touched row: decrements revert to the pre-cook row object,
      // deleted rows re-insert, created leftover rows drop.
      const touched = undo.prevRows.size + undo.inserts.length + undo.removes.size;
      if (touched > 0) {
        const cleared = undo.inserts.length;
        const adjusted = undo.prevRows.size;
        const parts = [];
        if (adjusted > 0) parts.push(`${adjusted} adjusted`);
        if (cleared > 0) parts.push(`${cleared} cleared`);
        if (leftoverSaved) parts.push("leftover saved");
        pushToast?.(
          `Kitchen updated — ${parts.join(" · ")}.`,
          {
            kind: "info",
            emoji: "",
            undo: () => {
              setPantry?.(prev => {
                const next = prev
                  .filter(r => !undo.removes.has(r.id))
                  .map(r => undo.prevRows.has(r.id) ? undo.prevRows.get(r.id) : r);
                for (const row of undo.inserts) {
                  if (!next.some(r => r.id === row.id)) next.push(row);
                }
                return next;
              });
            },
          }
        );
      }

      // Auto-migration toasts — selective per-row location undo for
      // opened-shelf-stable rows the cook moved to the fridge.
      for (const m of cookMigrations) {
        pushToast?.(
          `Moved to fridge — opened ${openedNounForState(m.state)} keep longer cold.`,
          {
            kind: "info",
            emoji: "",
            undo: () => {
              setPantry?.(prev => prev.map(r =>
                r.id === m.pantryRowId
                  ? { ...r, location: m.previousLocation, tileId: m.previousTileId }
                  : r
              ));
            },
          }
        );
      }
    }

    setSaving(false);

    // Gate onFinish on the summary overlay. If award_xp landed, surface
    // the beat-sequenced reveal first; otherwise finish immediately.
    if (cookLogId) {
      setSummaryCookLogId(cookLogId);
    } else {
      onFinish?.({ saved: true, rating });
    }
  };

  // ── shared modal shell ───────────────────────────────────────────────────
  const shell = (children) => (
    <div style={{ position:"fixed", inset:0, background:COLOR.night, zIndex:220, maxWidth:480, margin:"0 auto", display:"flex", flexDirection:"column", minHeight:0, overflow:"hidden" }}>
      {/* Persistent bypass — log the meal but leave the kitchen amounts
          untouched. Available on every phase so a cook who doesn't want
          auto-deduction can leave at any point. Hidden while saving and
          on the post-save revise offer (the cook is already logged —
          tapping it there would double-insert the cook_log). */}
      {!saving && phase !== "reviseOffer" && (
        <button
          type="button"
          onClick={() => save({ skipKitchen: true })}
          className="mcm-focusable"
          style={{
            position:"absolute", top:12, right:12, zIndex:5,
            background: COLOR.soil, border:`1px solid ${COLOR.goldDim}`,
            color: COLOR.dim, fontFamily:FONT.mono, fontSize:12, fontWeight:600,
            letterSpacing:"0.06em", padding:"7px 12px", borderRadius:999,
            cursor:"pointer", touchAction:"manipulation",
          }}
          title="Log this meal but leave your kitchen amounts untouched"
        >
          SKIP · KEEP KITCHEN
        </button>
      )}
      {children}
      <style>{`
        @keyframes fall { 0%{transform:translateY(-120px) rotate(0deg); opacity:0} 10%{opacity:1} 100%{transform:translateY(110vh) rotate(520deg); opacity:0.9} }
        @keyframes pulse-xp { 0%,100%{transform:scale(1); text-shadow:0 0 30px #f5c84266} 50%{transform:scale(1.08); text-shadow:0 0 50px #f5c842aa} }
        @keyframes rise { from{opacity:0; transform:translateY(16px)} to{opacity:1; transform:translateY(0)} }
      `}</style>
    </div>
  );

  // Summary overlay short-circuits the phase tree once save() has
  // landed. The summary's onClose calls onFinish so the parent's
  // navigation only fires after the beat sequence wraps.
  if (summaryCookLogId) {
    return (
      <CookCompleteSummary
        cookLogId={summaryCookLogId}
        onClose={() => {
          setSummaryCookLogId(null);
          // Revise-for-next-time offer — only when the cook left
          // notes (they ARE the revision feedback) and the parent
          // wired a handler. The offer fires no AI itself; it just
          // pre-fills the revise sheet.
          if (notes.trim() && onReviseRecipe) {
            setPhase("reviseOffer");
          } else {
            onFinish?.({ saved: true, rating });
          }
        }}
      />
    );
  }

  // ── phase: revise offer (post-summary, notes present) ───────────────────
  if (phase === "reviseOffer") {
    // Severity context rides along for rough/meh cooks so the model
    // knows how hard to lean into the fix.
    const ratingDef = RATINGS.find((r) => r.id === rating);
    const seedText = (rating === "rough" || rating === "meh") && ratingDef
      ? `${ratingDef.label} — ${notes.trim()}`
      : notes.trim();
    return shell(
      <CookReviseOffer
        recipeTitle={recipe?.title || null}
        notes={notes.trim()}
        onRevise={() => {
          onReviseRecipe?.(seedText);
          onFinish?.({ saved: true, rating });
        }}
        onSkip={() => onFinish?.({ saved: true, rating })}
      />
    );
  }

  // ── phase 1: celebrate ───────────────────────────────────────────────────
  if (phase === "celebrate") {
    // 28 confetti pieces, scattered deterministically so they don't re-roll
    // on every re-render.
    const confetti = Array.from({ length: 28 }, (_, i) => ({
      left: `${(i * 37) % 100}%`,
      delay: `${(i % 14) * 0.12}s`,
      color: [COLOR.gold,COLOR.leaf,COLOR.sky,COLOR.amber,CHIP_TONES.category.fg,COLOR.plum][i % 6],
      size: 6 + (i % 4) * 2,
      rot: (i * 47) % 360,
    }));
    return shell(
      <>
        <div style={{ position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden" }}>
          {confetti.map((c, i) => (
            <span key={i} style={{
              position:"absolute", top:-20, left:c.left,
              width:c.size, height:c.size*1.6, background:c.color,
              transform:`rotate(${c.rot}deg)`,
              animation:`fall 2.6s ${c.delay} ease-in forwards`,
              borderRadius:1,
            }} />
          ))}
        </div>
        <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:40, textAlign:"center", position:"relative", zIndex:1 }}>
          <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.leaf, letterSpacing:"0.18em", marginBottom:14, animation:"rise 0.4s ease" }}>
            ✓ MEAL COMPLETE
          </div>
          <div style={{ fontSize:80, marginBottom:12, animation:"rise 0.5s 0.1s ease backwards" }}>{recipe.emoji || "🍽️"}</div>
          <h1 style={{ fontFamily:FONT.serif, fontSize:34, fontWeight:300, fontStyle:"italic", color:COLOR.ink, marginBottom:6, animation:"rise 0.5s 0.2s ease backwards" }}>
            You cooked {recipe.title}!
          </h1>
          <p style={{ fontFamily:FONT.sans, fontSize:14, color:COLOR.dim, marginBottom:28, animation:"rise 0.5s 0.3s ease backwards" }}>
            That's a whole meal out of your kitchen. Take a breath.
          </p>
          <div style={{ fontFamily:FONT.mono, fontSize:44, color:COLOR.gold, fontWeight:600, animation:"pulse-xp 1.6s ease-in-out infinite" }}>
            +{xp} XP
          </div>
          <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.sub, letterSpacing:"0.15em", marginTop:4, marginBottom:36 }}>
            SKILL POINTS EARNED
          </div>
          <button
            onClick={() => { setSkipKitchen(false); setPhase("kitchenUpdate"); }}
            className="mcm-focusable"
            style={{ width:"100%", maxWidth:320, padding:"16px", background:COLOR.gold, color:COLOR.night, border:"none", borderRadius:14, fontFamily:FONT.mono, fontSize:13, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer", boxShadow:"0 0 30px #f5c84244", touchAction:"manipulation" }}
          >
            UPDATE KITCHEN →
          </button>
          {/* Skip-inventory gate. Some cooks don't want amounts auto-
              reduced (mid-cook eyeballing, shared kitchens, partial
              batches). They still get to rate — skipping inventory is
              NOT skipping the meal log. */}
          <button
            onClick={() => { setSkipKitchen(true); setPhase("rateIt"); }}
            className="mcm-focusable"
            style={{ marginTop:12, width:"100%", maxWidth:320, padding:"13px", background:"transparent", color:COLOR.dim, border:`1px solid ${COLOR.border}`, borderRadius:12, fontFamily:FONT.mono, fontSize:12, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer", touchAction:"manipulation" }}
          >
            SKIP KITCHEN · JUST RATE IT →
          </button>
          {/* Fork-to-new-recipe affordance. Only surfaces when the user
              actually made swaps or skips during this cook. The original
              recipe is never touched. */}
          {canOfferFork && (
            <button
              onClick={() => { setForkOpen(true); setForkError(null); }}
              className="mcm-focusable"
              style={{ marginTop:12, width:"100%", maxWidth:320, padding:"13px", background:"transparent", color:COLOR.gold, border:`1px solid ${COLOR.goldDim}`, borderRadius:12, fontFamily:FONT.mono, fontSize:12, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer", touchAction:"manipulation" }}
            >
              ↔ SAVE CHANGES AS NEW RECIPE
            </button>
          )}
        </div>
        {forkOpen && (
          <div
            onClick={() => { if (forkState !== "pending") setForkOpen(false); }}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", padding:20, zIndex:10 }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ width:"100%", maxWidth:380, background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:16, padding:"22px 20px" }}
            >
              <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:6 }}>
                FORK RECIPE
              </div>
              <div style={{ fontFamily:FONT.serif, fontSize:22, fontWeight:300, color:COLOR.ink, marginBottom:6 }}>
                Save {swapCount} swap{swapCount === 1 ? "" : "s"} as a new recipe
              </div>
              <div style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.dim, marginBottom:16, lineHeight:1.5 }}>
                The original "{recipe?.title}" stays as-is. This saves an iteration with your swaps baked in.
              </div>
              <label style={{ display:"block", fontFamily:FONT.mono, fontSize:12, color:COLOR.dim, letterSpacing:"0.1em", marginBottom:6 }}>
                NAME
              </label>
              <input
                type="text"
                value={forkName}
                onChange={(e) => setForkName(e.target.value)}
                disabled={forkState === "pending" || forkState === "saved"}
                style={{ width:"100%", padding:"12px 14px", background:COLOR.deep, border:`1px solid ${COLOR.border}`, borderRadius:10, fontFamily:FONT.sans, fontSize:15, color:COLOR.ink, boxSizing:"border-box" }}
              />
              {forkError && (
                <div style={{ marginTop:10, fontFamily:FONT.sans, fontSize:12, color:COLOR.rose }}>
                  {forkError}
                </div>
              )}
              <div style={{ display:"flex", gap:10, marginTop:18 }}>
                <button
                  onClick={() => setForkOpen(false)}
                  disabled={forkState === "pending"}
                  className="mcm-focusable"
                  style={{ flex:1, padding:"12px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, color:COLOR.dim, borderRadius:10, fontFamily:FONT.mono, fontSize:12, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer", touchAction:"manipulation" }}
                >
                  CANCEL
                </button>
                <button
                  onClick={handleForkSave}
                  disabled={forkState === "pending" || forkState === "saved" || !forkName.trim()}
                  className="mcm-focusable"
                  style={{ flex:2, padding:"12px", background: forkState === "saved" ? "#1a3a1a" : COLOR.gold, color: forkState === "saved" ? COLOR.leaf : COLOR.night, border:"none", borderRadius:10, fontFamily:FONT.mono, fontSize:12, fontWeight:600, letterSpacing:"0.08em", cursor: forkState === "pending" ? "wait" : "pointer", touchAction:"manipulation" }}
                >
                  {forkState === "pending" ? "SAVING…"
                    : forkState === "saved"  ? "✓ SAVED"
                    : "SAVE AS NEW RECIPE"}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── phase 2: kitchen update (one reconcile screen) ───────────────────────
  if (phase === "kitchenUpdate") {
    const plan = buildRemovalPlan(usedItems, extraRemovals, pantry);
    return shell(
      <CookKitchenUpdate
        recipe={recipe}
        usedItems={usedItems}
        setUsedItems={setUsedItems}
        extraRemovals={extraRemovals}
        setExtraRemovals={setExtraRemovals}
        pantry={pantry}
        plan={plan}
        pickerForIdx={pickerForIdx}
        setPickerForIdx={setPickerForIdx}
        leftoverChoice={leftoverChoice}
        setLeftoverChoice={setLeftoverChoice}
        leftoverLocation={leftoverLocation}
        setLeftoverLocation={setLeftoverLocation}
        leftoverMode={leftoverMode}
        setLeftoverMode={setLeftoverMode}
        leftoverFraction={leftoverFraction}
        setLeftoverFraction={setLeftoverFraction}
        leftoverServings={leftoverServings}
        setLeftoverServings={setLeftoverServings}
        leftoverCustomName={leftoverCustomName}
        setLeftoverCustomName={setLeftoverCustomName}
        isCompoundProduce={isCompoundProduce}
        manualProduces={manualProduces}
        setManualProduces={setManualProduces}
        producesPickerOpen={producesPickerOpen}
        setProducesPickerOpen={setProducesPickerOpen}
        producesSearch={producesSearch}
        setProducesSearch={setProducesSearch}
        producesMatches={producesMatches}
        onAddToShopping={setShoppingList ? addOutToShopping : null}
        shoppingAddedKeys={shoppingAddedKeys}
        stepLabel="STEP 1 OF 2"
        onBack={() => setPhase("celebrate")}
        onContinue={() => { setSkipKitchen(false); setPhase("rateIt"); }}
        onSkipKitchen={() => { setSkipKitchen(true); setPhase("rateIt"); }}
      />
    );
  }

  // ── phase 3: rate it (one social screen) ─────────────────────────────────
  if (phase === "rateIt") {
    const nPreview = recipeNutrition
      ? recipeNutrition(recipe, { pantry, brandNutrition, getInfo: ingredientInfo?.getInfo })
      : null;
    return shell(
      <CookRateIt
        connections={connections}
        selectedDiners={selectedDiners}
        toggleDiner={toggleDiner}
        ratings={RATINGS}
        rating={rating}
        setRating={setRating}
        notes={notes}
        setNotes={setNotes}
        servingsPerEater={servingsPerEater}
        setServingsPerEater={setServingsPerEater}
        nutritionPreview={nPreview}
        xp={xp}
        saving={saving}
        error={error}
        stepLabel="STEP 2 OF 2"
        onBack={() => setPhase(skipKitchen ? "celebrate" : "kitchenUpdate")}
        onSave={() => (SHOW_NUTRITION_DEBUG ? setPhase("nutritionDebug") : save({ skipKitchen }))}
      />
    );
  }

  // ── DEBUG phase: per-ingredient calorie breakdown ────────────────────────
  // Diagnostic screen behind the mise_debug localStorage flag. Surfaces
  // how the resolver chain scored each recipe ingredient.
  if (phase === "nutritionDebug") {
    const bd = recipeNutritionBreakdown(recipe, { pantry, brandNutrition, getInfo: ingredientInfo?.getInfo });
    return shell(
      <div style={{ flex:1, display:"flex", flexDirection:"column", padding:"40px 24px 32px", overflowY:"auto" }}>
        <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.rose, letterSpacing:"0.15em", marginBottom:8 }}>
          DEBUG · CALORIE BREAKDOWN
        </div>
        <h2 style={{ fontFamily:FONT.serif, fontSize:26, fontWeight:300, fontStyle:"italic", color:COLOR.ink, marginBottom:4 }}>
          Where did the calories come from?
        </h2>
        <p style={{ fontFamily:FONT.sans, fontSize:12, color:COLOR.muted, marginBottom:16 }}>
          Per-ingredient resolver trace. Rows without a macro contribution show why.
        </p>

        <div style={{ padding:"10px 12px", background:COLOR.deep, border:`1px solid ${COLOR.border}`, borderRadius:10, marginBottom:12, fontFamily:FONT.mono, fontSize:12, color:COLOR.dim }}>
          <div>TOTAL: {Math.round(bd.total.kcal)} kcal · {Math.round(bd.total.protein_g)}p · {Math.round(bd.total.carb_g)}c · {Math.round(bd.total.fat_g)}f</div>
          <div style={{ marginTop:2 }}>PER SERVING (÷ {bd.serves}): {Math.round(bd.perServing.kcal)} kcal</div>
          <div style={{ marginTop:2, color:COLOR.dim }}>
            COVERAGE: {bd.coverage.resolved} / {bd.coverage.total} ingredients
            {bd.coverage.resolved === 0 && <span style={{ color:COLOR.rose }}> · NOTHING RESOLVED — dashboard will skip this cook</span>}
          </div>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
          {bd.items.length === 0 && (
            <div style={{ padding:"12px", background:COLOR.roseDeep, border:`1px solid ${COLOR.roseDim}`, borderRadius:8, fontFamily:FONT.mono, fontSize:12, color:COLOR.rose }}>
              Recipe has no ingredients array.
            </div>
          )}
          {bd.items.map((row, i) => {
            const ok = row.kcal > 0;
            return (
              <div key={i} style={{
                padding:"10px 12px",
                background: ok ? COLOR.leafDeep : COLOR.roseDeep,
                border: `1px solid ${ok ? COLOR.leafDim : COLOR.roseDim}`,
                borderRadius:8,
                fontFamily:FONT.mono, fontSize:12, color:COLOR.dim,
              }}>
                <div style={{ display:"flex", justifyContent:"space-between", gap:8, marginBottom:4 }}>
                  <span style={{ color:COLOR.ink, fontWeight:600 }}>{row.name}</span>
                  <span style={{ color: ok ? COLOR.leaf : COLOR.rose }}>
                    {ok ? `+${Math.round(row.kcal)} kcal` : "— kcal"}
                  </span>
                </div>
                <div style={{ color:COLOR.dim, fontSize:12, lineHeight:1.5 }}>
                  <div>canonical: {row.canonicalId || "(none)"}{row.canonical ? ` → ${row.canonical}` : ""}</div>
                  <div>amount: {row.amount || "(none)"} {row.parsedQty ? `→ ${row.parsedQty.amount} ${row.parsedQty.unit}` : ""}</div>
                  <div>nutrition source: {row.source || "(none)"}{row.brand ? ` · brand=${row.brand}` : ""}{row.nutrition?.per ? ` · per=${row.nutrition.per}` : ""}</div>
                  {row.parsedQty?.unit === "count" && (
                    <div>grams/count: {Number.isFinite(row.countWeightG) && row.countWeightG > 0 ? `${Math.round(row.countWeightG)}g` : "(unset)"}</div>
                  )}
                  <div>factor: {row.factor == null ? "null" : row.factor.toFixed(3)}</div>
                  {row.reason && (
                    <div style={{ color:COLOR.rose, marginTop:2 }}>× {row.reason}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display:"flex", gap:10, marginTop:"auto" }}>
          <button onClick={() => setPhase("rateIt")}
            className="mcm-focusable"
            style={{ flex:1, padding:"14px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, fontFamily:FONT.mono, fontSize:12, color:COLOR.dim, cursor:"pointer", letterSpacing:"0.08em", touchAction:"manipulation" }}>
            ← BACK
          </button>
          <button onClick={() => save({ skipKitchen })} disabled={saving}
            className="mcm-focusable"
            style={{ flex:2, padding:"14px", background: saving ? COLOR.soil : COLOR.gold, border:"none", borderRadius:12, fontFamily:FONT.mono, fontSize:12, fontWeight:600, color: saving ? COLOR.sub : COLOR.night, cursor: saving ? "not-allowed" : "pointer", letterSpacing:"0.08em", touchAction:"manipulation" }}>
            {saving ? "SAVING..." : "SAVE →"}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
